<template>
  <div
    :style="styles"
    :class="cssContainer"
    class="promotion-tile"
  >
    <div
      :style="textColor"
      class="promotion-tile__content"
    >
      <CdnImage
        v-if="brand"
        class="promotion-tile__brand"
        :alt="brand.alt || brand.name"
        :src="brand.src || brand.logo"
        :style="brand.style"
        :sizes="brand.sizes"
        render-without-figure
        :lazy="promotionData.lazyLoading || false"
        :preload="!(promotionData.lazyLoading || false)"
      />
      <Heading
        v-if="headline"
        :tag="headingTag"
        class="promotion-tile__headline"
        :text="headline"
        :style="textColor"
      />
      <p
        v-else-if="subline"
        class="text promotion-tile__subline"
        v-html="subline"
      />

      <PromotionBadge
        v-if="promotionData.badge && !promotionData.isPromotionEnded"
        :badge="promotionData.badge"
        :modal-heading="promotionData.badeModal?.heading"
        :modal-texts="promotionData.badeModal?.texts"
        :offer="offer"
        class="promotion-tile__badge"
      />

      <div
        v-if="visual"
        class="promotion-tile__image-container"
      >
        <div class="promotion-tile__image">
          <CdnImage
            v-if="visual.srcSmall"
            :alt="visual.alt || ''"
            :src="visual.srcSmall"
            :src-md="visual.srcLarge"
            :src-sm="visual.srcMedium"
            :image-style="visual.style"
            :sizes="visual.sizes"
            class="promotion-tile__visual"
            :lazy="promotionData.lazyLoading || false"
            :preload="!(promotionData.lazyLoading || false)"
          />
        </div>
      </div>
      <Badge
        v-if="promotionData.isPromotionEnded"
        class="promotion-tile__badge promotion-tile__badge-ended text--bold"
      >
        Aktion beendet
      </Badge>
      <div
        class="promotion-tile__button-container"
        :class="buttonContainer"
      >
        <div
          v-if="!promotionData.isPromotionEnded"
          class="promotion-tile__buttons"
        >
          <div
            v-for="(button, index) in buttons"
            :key="index"
          >
            <Modal
              v-if="promotionData.treueshopModal"
              :custom-class="`promotion-tile__treueshop-modal promotion-tile__treueshop-modal--${promotionData.treueshopModal.id}`"
            >
              <template #trigger="{ open }">
                <LinkButton
                  tag="button"
                  :primary="button.primary"
                  :secondary="button.secondary"
                  :outline="button.transparent"
                  :outline-light="button.transparentLight"
                  :style="button.style"
                  class="promotion-tile__button"
                  @click.native="open"
                >
                  {{ button.label }}
                </LinkButton>
              </template>
              <SvgIcon
                :image="treueshopImage"
                class="promotion-tile__treueshop-modal-image"
                no-icon-class
              />
              <Paragraph
                :text="promotionData.treueshopModal.subheading"
                class="promotion-tile__treueshop-modal-subheading"
              />
              <Heading
                :text="promotionData.treueshopModal.heading"
                class="promotion-tile__treueshop-modal-heading"
                tag="h3"
                size="md"
                font-weight="ultra"
                align="center"
              />
              <Paragraph
                :text="promotionData.treueshopModal.copy"
                class="promotion-tile__treueshop-modal-copy"
                align="center"
              />
              <template #footer="{ close }">
                <LinkButton
                  class="promotion-tile__treueshop-modal-button"
                  tag="button"
                  outline
                  @click.native="close"
                >
                  Schließen
                </LinkButton>
              </template>
            </Modal>
            <LinkButton
              v-else-if="button.url"
              :data-tealium-rel="buttonDataTealiumRel(button.label)"
              :primary="button.primary"
              :secondary="button.secondary"
              :outline="button.transparent"
              :outline-light="button.transparentLight"
              :style="button.style"
              :tag="button.tag"
              :to="button.url"
              :external="button.external"
              class="promotion-tile__button"
            >
              {{ button.label }}
            </LinkButton>
          </div>
        </div>
      </div>
    </div>
    <CdnImage
      v-if="backgroundImage"
      class="promotion-tile__background-image"
      :alt="backgroundImage.alt"
      :src="backgroundImage.srcSmall"
      :src-sm="backgroundImage.srcMedium"
      :src-md="backgroundImage.srcLarge"
      :sizes="backgroundImage.sizes"
      :style="backgroundImage.style"
      :do-not-convert="!backgroundImage.sizes"
      :lazy="promotionData.lazyLoading || false"
      :preload="!(promotionData.lazyLoading || false)"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, type PropType } from 'vue';
import PromotionBadge from '@/components/promotion-badge.vue';
import {
  Badge,
  Heading,
  Paragraph,
  LinkButton,
  slugify,
  Modal,
} from '@i22-td-smarthome/component-library';
import CdnImage from '@/components/cdn-image.vue';

import { addLogo, getBrand } from '~/lib/brand';
import treueshopImage from '@/assets/images/rabatt-aktion-logo.svg';
import type { PromotionBadgeType, PromotionVisual, StageSliderBrand } from '~/lib/promotions-type';
import type { ListOffer } from '~/lib/goliath/offers';
import { getMainImageFromOffer } from '~/lib/goliath-media';

export type PromotionButton = {
  label: string;
  url: string;
  tag?: 'nuxt-link' | 'button' | 'a';
  style?: string;
  isExternal?: boolean;
  primary?: boolean;
  secondary?: boolean;
  transparent?: boolean;
  transparentLight?: boolean;
};

export type Promotion = {
  tileSize?: 'half' | 'full';
  isPromotionEnded: boolean;
  brand?: StageSliderBrand;
  offer?: ListOffer;
  customClass?: string;
  buttonsInline?: boolean;
  textColor?: string;
  headline?: string;
  subline?: string;
  lazyLoading?: boolean;
  visual?: PromotionVisual;
  treueshopModal?: {
    id: string;
    subheading?: string;
    heading: string;
    copy?: string;
  };
  background?: {
    color?: string;
    image?: PromotionVisual;
  };
  badge?: PromotionBadgeType;
  badeModal?: {
    texts: string[];
    heading?: string;
  };
  buttons?: PromotionButton[];
};

const props = defineProps({
  promotionData: {
    type: Object as PropType<Promotion>,
    required: true,
  },
  headingTag: {
    type: String,
    default: 'h2',
  },
});

const offer = computed<ListOffer | undefined>(() => props.promotionData.offer);

const backgroundImage = computed<PromotionVisual | undefined>(
  () => props.promotionData.background?.image
);

const styles = computed(() => {
  const backgrund =
    !props.promotionData.background || backgroundImage.value
      ? '#FFF'
      : props.promotionData.background;
  const entries = [`background: ${backgrund}`];
  if (props.promotionData.styles) {
    entries.push(props.promotionData.styles);
  }
  return entries.join(';');
});

const cssContainer = computed(() => {
  const modifier = [];
  if (!props.promotionData.tileSize) {
    modifier.push('promotion-tile--half');
  } else {
    modifier.push(`promotion-tile--${props.promotionData.tileSize}`);
  }
  if (props.promotionData.customClass) {
    if (Array.isArray(props.promotionData.customClass)) {
      props.promotionData.customClass.forEach((mod) => modifier.push(`promotion-tile--${mod}`));
    } else {
      modifier.push(`promotion-tile--${props.promotionData.customClass}`);
    }
  }
  if (props.promotionData.isPromotionEnded) {
    modifier.push('promotion-tile--disabled');
  }
  return modifier;
});

const buttonContainer = computed(() =>
  props.promotionData.buttonsInline ? 'promotion-tile__button-container--inline' : ''
);

const textColor = computed(() => {
  if (!props.promotionData.textColor) return undefined;
  return `color: ${props.promotionData.textColor}`;
});

const headline = computed(() => {
  if (props.promotionData.headline) return props.promotionData.headline;
  if (!offer.value) return undefined;
  return offer.value.product.name;
});

const subline = computed(() => props.promotionData?.subline);

const slug = computed(() => {
  if (!offer.value) return undefined;
  return offer.value.product.slug;
});

const buttons = computed<PromotionButton[]>(() => {
  if (!props.promotionData.buttons) {
    if (slug.value) {
      return [
        {
          label: 'Zum Angebot',
          outline: true,
          tag: 'nuxt-link',
          url: `/geraete/${slug.value}`,
        },
      ];
    }
    return undefined;
  }
  return props.promotionData.buttons;
});

// srcMedium and srcLarge is possible as well
const visual = computed(() => {
  const goliathImage = offer.value ? getMainImageFromOffer(offer.value) : undefined;
  if (goliathImage && !props.promotionData.visual?.srcSmall) {
    return {
      alt: goliathImage.name,
      srcSmall: goliathImage.small,
      srcMedium: goliathImage.medium,
      srcLarge: goliathImage.large,
      ...(props.promotionData.visual || {}),
    };
  }
  return props.promotionData.visual;
});

const brand = computed(() => {
  if (props.promotionData.brand?.slug) {
    const brandData = addLogo({
      slug: props.promotionData.brand.slug,
      name: props.promotionData.brand.alt,
    });
    if (brandData) {
      return {
        ...brandData,
        ...props.promotionData.brand,
        style: `aspect-ratio: ${brandData.aspectRatio};${props.promotionData.brand.style}`,
      };
    }
  }

  if (offer.value) {
    const brandData = getBrand(offer.value);
    if (brandData) {
      return {
        ...brandData,
        ...(props.promotionData.brand || {}),
        style: `aspect-ratio: ${brandData.aspectRatio};${props.promotionData.brand?.style}`,
      };
    }
  }

  return undefined;
});

const buttonDataTealiumRel = (text) => `content.button.${slugify(text)}`;

defineExpose({
  styles,
  cssContainer,
  textColor,
  subline,
  headline,
  buttons,
  visual,
  slug,
});
</script>

<style lang="scss" scoped>
@use 'assets/base' as *;

.promotion-tile {
  display: grid;
  flex-direction: column;
  justify-content: center;
  width: 100%;
  position: relative;
  overflow: hidden;

  @include mq($mq-sm) {
    flex-direction: row;
  }

  &--half {
    min-height: 466px;
    @include mq($mq-sm) {
      min-height: 595px;
      flex: 0 0 calc(50% - $grid-base / 2);
    }
  }

  &--full {
    min-height: initial;
  }

  &--row-reverse {
    @include mq($mq-sm) {
      flex-direction: row-reverse;
    }
  }

  &__buttons {
    display: flex;
    flex-direction: row;
    grid-gap: $grid-base;
  }

  &__button-container {
    &--inline {
      position: absolute;
      bottom: $grid-base * 3;
    }
  }

  &__content,
  &__background-image {
    grid-row: 1;
    grid-column: 1;
  }

  &__content {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%; /* IE Fix */
    grid-gap: $grid-base;
    position: relative;
    z-index: 1;
    padding: $grid-base * 2 $grid-base;
  }

  &__background-image {
    position: relative;
    display: block;
    width: 100%;
    height: 100%;
    pointer-events: none;

    :deep(img) {
      object-fit: cover;
      display: block;
      width: 100%;
      height: 100%;
    }
  }

  &__modal-paragraph {
    text-align: center;
  }

  &__brand {
    height: 40px;
    display: inline-block;
  }

  &__treueshop-modal {
    :deep(.modal__inner) {
      display: flex;
      flex-direction: column;
      align-items: center;

      @include mq($mq-sm) {
        padding: 4 * $grid-base;
      }
    }

    &-image {
      width: 270px;
      margin-bottom: 2 * $grid-base;
    }

    &-subheading {
      font-weight: 800;
      @include font-size(24px, 29px);
      color: color(magenta, 1000);
      margin-bottom: 0;
      text-align: center;
    }

    &-heading {
      margin-bottom: $grid-base;
    }

    &-list {
      text-align: left;
      padding: 0 $grid-base;
      @include font-size(16px, 22px);

      @include mq($mq-sm) {
        padding: 0 2 * $grid-base;

        li {
          margin-bottom: 0.5 * $grid-base;
        }
      }
    }
  }

  &__subline {
    line-height: 1em;
    font-size: var(--teaser-subline-xs, 1em);
    text-align: center;
    @include mq($mq-sm) {
      font-size: var(--teaser-subline-sm, 1.3rem);
    }
  }

  &__headline {
    line-height: 1em;
    font-size: var(--teaser-heading-xs, 1.5em);

    width: 100%;
    text-transform: uppercase;
    text-align: center;
    font-weight: 700;

    @include mq($mq-sm) {
      font-size: var(--teaser-heading-sm, 2em);
    }
  }

  &__image {
    display: flex;
    justify-content: center;
    flex-direction: row;
    align-items: flex-end;
    height: 100%;

    &-container {
      position: relative;
      width: 100%;
      flex: 1 1 100%;
      margin-inline: $grid-base * -1;
    }
  }

  &__badge {
  }

  &__badge-ended {
    top: 50%;
  }

  //Promotion Ended

  &--disabled {
    color: color(grey, 16000);
    .promotion-tile {
      &__image-container,
      &__background-image {
        filter: grayscale(75%);
        opacity: 0.5;
      }
      &__headline {
        color: color(grey, 16000);
      }
    }
  }

  &--gardena {
    .promotion-tile__content {
      justify-content: space-between;

      @include mq($mq-sm) {
        justify-content: flex-end;
      }
    }

    .promotion-tile__badge {
      left: 5%;
      top: 20%;

      @include mq($mq-md) {
        left: 20%;
      }
    }

    .promotion-tile__brand {
      @include mq($mq-sm) {
        position: absolute;
        left: 5%;
      }
    }
  }
}
</style>
